import { lazy, useEffect, useState } from 'react';
import { BlockWrapper, defineBlock, smartProps, useInspectorControl } from '@smartfire/js-block-engine';
import { EditableHeading } from '@smartfire/components';
import { Tabs, Tab } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Scrollbar } from 'swiper/modules';
import { __ } from '../../shared/tools';
import ArrowLeft from '../../assets/icons/arrow_carrousel_left';
import ArrowRight from '../../assets/icons/arrow_carrousel_right';
import clsx from 'clsx';

import 'swiper/css';
import 'swiper/css/scrollbar';
import style from './style.module.scss';

const Inspector = lazy(() => import('./inspector'));

const HomeSliderProducts = ({ categories = [], per_page = 5 }) => {
    useInspectorControl(Inspector);
    const [currentCategory, setCurrentCategory] = useState(null);

    useEffect(() => {
        categories && categories.length > 0 && setCurrentCategory(categories[0].id);
    }, [categories]);

    const handleChange = (event, val) => {
        setCurrentCategory(val);
    };

    return (
        <BlockWrapper.Section className={style.block}>
            <div className={style.content}>
                <EditableHeading smartProp="title" className={style.title} placeholder="Découvrez..." />
                <div className={style.header}>
                    {categories.length > 0 && (
                        <Tabs
                            value={currentCategory ?? categories[0].id}
                            onChange={handleChange}
                            aria-label=""
                            className={style.categories}
                        >
                            {categories.map((category) => (
                                <Tab
                                    key={category.id}
                                    label={category.name}
                                    id={category.id}
                                    value={category.id}
                                    className={clsx(style.category, currentCategory === category.id && style.active)}
                                />
                            ))}
                        </Tabs>
                    )}
                    <div className={style.navigation}>
                        <ArrowLeft
                            className={clsx(style.arrow, style.prev, 'home-categories-swiper-button-prev')}
                            role="button"
                        />
                        <ArrowRight
                            className={clsx(style.arrow, style.next, 'home-categories-swiper-button-next')}
                            role="button"
                        />
                    </div>
                </div>
                <CategoriesList categories={categories.find((category) => category.id === currentCategory)?.children} />
            </div>
        </BlockWrapper.Section>
    );
};

const CategoriesList = ({ categories = [] }) => {
    const swiperProps = {
        spaceBetween: 45,
        slidesPerView: 1.3,
        className: style.swiper,
        autoplay: { delay: 3000, pauseOnMouseEnter: true },
        speed: 600,
        navigation: {
            nextEl: '.home-categories-swiper-button-next',
            prevEl: '.home-categories-swiper-button-prev',
            disabledClass: style.disabled,
            hiddenClass: style.disabled,
        },
        scrollbar: {
            draggable: true,
        },
        breakpoints: {
            767: {
                slidesPerView: 3.2,
            },
        },
        modules: [Autoplay, Navigation, Scrollbar],
    };

    return categories.length > 0 ? (
        <div className={style.carrousel} style={{ '--swiper-scrollbar-border-radius': 0 }}>
            <Swiper {...swiperProps}>
                {categories.map((category, i) => {
                    return (
                        <SwiperSlide className={style.slide} key={category.id}>
                            <CategoryTile category={category} />
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    ) : (
        <p className={style.empty}>{__('Aucun résultat')}</p>
    );
};

export const CategoryTile = ({ category, ...props }) => {
    return (
        <a href={category.url} className={style.category} {...props}>
            {category.image && <img src={category.image} className={style.image} alt={category.name} />}
            <p className={style.name}>{category.name}</p>
        </a>
    );
};

export default defineBlock({
    Block: HomeSliderProducts,
    setup: {
        title: 'Slider Catégories Produits',
    },
    smartProps: {
        title: smartProps.object.withDefault({
            headingLevel: 2,
            content: '',
        }),
        categories_ids: smartProps.array.withDefault([]),
        categories: smartProps.array.contentServerSide(),
    },
});
